import "./MonthlyConfiguration.css";
import React, { useState, useContext } from "react";
import axios from "axios";
import AuthContext from "../AuthContext";
import MonthSelector from "./MonthSelector";
import { Tabs, Tab } from "@mui/material";

function MonthlyConfiguration() {
    const { user } = useContext(AuthContext);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [budgetValues, setBudgetValues] = useState({ factory: "" });
    const [productStockBudget, setProductStockBudget] = useState([]);
    const [productStockFactory, setProductStockFactory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [selectedTab, setSelectedTab] = useState(0);
    const apiUrl = process.env.REACT_APP_API_URL;

    const predefinedProducts = [
        "Chol250", "Chol500", "Chol500 Disp",
        "Transisoft", "Transisoft Disp", "Simalvia", "Diabact x1",  "Diabact x10"
    ];

    const fetchBudget = async (month) => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/api/get_monthly_budget`, {
                params: { month },
                withCredentials: true,
            });
            if (response.status === 200) {
                setBudgetValues({ factory: response.data.budget || 0 });
            }
        } catch (error) {
            console.error("Error fetching budget:", error);
            setMessage("Error fetching budget. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchProductStockBudget = async (month) => {
        try {
            const response = await axios.get(`${apiUrl}/api/get_product_stock_budget`, {
                params: { month },
                withCredentials: true,
            });
    
            if (response.status === 200 && response.data.stock.length > 0) {
                // Ensure unitSellingPrice is included, even if null from API
                setProductStockBudget(
                    response.data.stock.map((product) => ({
                        product: product.product,
                        quantity: product.quantity,
                        cash: product.cash,
                        unitSellingPrice: product.unit_selling_price ?? 0, // Default to 0 if null
                    }))
                );
            } else {
                setProductStockBudget(
                    predefinedProducts.map((product) => ({
                        product,
                        quantity: 0,
                        cash: 0,
                        unitSellingPrice: 0,
                    }))
                );
            }
        } catch (error) {
            console.error("Error fetching product stock:", error);
            setProductStockBudget(
                predefinedProducts.map((product) => ({
                    product,
                    quantity: 0,
                    cash: 0,
                    unitSellingPrice: 0,
                }))
            );
        }
    };
    

    const fetchProductStockFactory = async (month) => {
        try {
            const response = await axios.get(`${apiUrl}/api/get_product_stock_factory`, {
                params: { month },
                withCredentials: true,
            });
            if (response.status === 200 && response.data.stock.length > 0) {
                setProductStockFactory(response.data.stock);
            } else {
                setProductStockFactory(predefinedProducts.map(product => ({ product, quantity: 0 })));
            }
        } catch (error) {
            console.error("Error fetching product stock:", error);
            setProductStockFactory(predefinedProducts.map(product => ({ product, quantity: 0 })));
        }
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        setBudgetValues({ factory: "" });
        setProductStockBudget(predefinedProducts.map(product => ({ product, quantity: 0, cash:0, unitSellingPrice: 0 })));
        setProductStockFactory(predefinedProducts.map(product => ({ product, quantity: 0 })));
        fetchBudget(month);
        fetchProductStockBudget(month);
        fetchProductStockFactory(month);
    };

    const handleBudgetChange = (field, value) => {
        setBudgetValues((prevValues) => ({
            ...prevValues,
            [field]: value,
        }));
    };

    const handleStockBudgetChange = (index, field, value) => {
        setProductStockBudget(prevStock =>
            prevStock.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    };

    const handleStockFactoryChange = (index, value) => {
        setProductStockFactory(prevStock =>
            prevStock.map((item, i) => (i === index ? { ...item, quantity: value } : item))
        );
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const handleSubmit = async () => {
        if (!selectedMonth || !budgetValues.factory) {
            setMessage("Please select a month and enter a budget before submitting.");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            await axios.post(`${apiUrl}/api/save_monthly_budget`, {
                user_id: user.id,
                month: selectedMonth,
                budget: parseFloat(budgetValues.factory),
            }, {
                withCredentials: true,
            });

            await axios.post(`${apiUrl}/api/save_product_stock_budget`, {
                user_id: user.id,
                month: selectedMonth,
                stock: productStockBudget,
            }, {
                withCredentials: true,
            });

            await axios.post(`${apiUrl}/api/save_product_stock_factory`, {
                user_id: user.id,
                month: selectedMonth,
                stock: productStockFactory,
            }, {
                withCredentials: true,
            });

            setMessage(`Budget and stock successfully saved for ${selectedMonth}`);
        } catch (error) {
            console.error("Error saving data:", error);
            setMessage("Error saving data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="monthly-config-container">
            <h3>Monthly Configuration</h3>

            <div className="month-selector-container">
                <MonthSelector selectedMonth={selectedMonth} onMonthChange={handleMonthChange} />
            </div>

            {selectedMonth && (
                <>
                    <Tabs value={selectedTab} onChange={handleTabChange} centered>
                        <Tab label="Budget & Stock Configuration" />
                        <Tab label="Factory Stock Configuration" />
                    </Tabs>

                    {selectedTab === 0 && (
                        <>
                            <div className="budget-container">
                                <h3>Set Budget for {selectedMonth}</h3>
                                <label>Budget (£)</label>
                                <input
                                    type="number"
                                    value={budgetValues.factory}
                                    onChange={(e) => handleBudgetChange("factory", e.target.value)}
                                    placeholder="Enter Budget for the month"
                                />
                            </div>
                            <div className="stock-inputs">
                                <h3>Budget Stock for {selectedMonth}</h3>
                                <table className="stock-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Budget Quantity</th>
                                            <th>Budget Cash</th>
                                            <th>Unit Selling Price (£)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productStockBudget.map((product, index) => (
                                            <tr key={index}>
                                                <td>{product.product}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={product.quantity}
                                                        onChange={(e) => handleStockBudgetChange(index, "quantity", e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={product.cash}
                                                        onChange={(e) => handleStockBudgetChange(index, "cash", e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={product.unitSellingPrice}
                                                        onChange={(e) => handleStockBudgetChange(index, "unitSellingPrice", e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {selectedTab === 1 && (
                        <div className="stock-inputs">
                            <h3>Factory Stock for {selectedMonth}</h3>
                            <table className="stock-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Budget Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productStockFactory.map((product, index) => (
                                        <tr key={index}>
                                            <td>{product.product}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={product.quantity}
                                                    onChange={(e) => handleStockFactoryChange(index, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <button className="submit-button" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Saving..." : "Submit"}
                    </button>

                    {message && <p className={`message ${message.includes("successfully") ? "success" : "error"}`}>{message}</p>}
                </>
            )}
        </div>
    );
}

export default MonthlyConfiguration;
